import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, Lock, Smartphone, ShieldCheck, CloudUpload, 
  AlertTriangle, Eye, Wifi, RefreshCw, User, Globe, Users,
  CheckCircle, Clock, ChevronRight
} from 'lucide-react';
import { useProgress } from '../../context/ProgressContext';
import { usePurchase } from '../../context/PurchaseContext';
import { cn } from '../../lib/utils';

const iconMap = {
  BookOpen, Lock, Smartphone, ShieldCheck, CloudUpload,
  AlertTriangle, Eye, Wifi, RefreshCw, User, Globe, Users,
};

// Module 1 is free
const FREE_MODULE_IDS = [1];

export function ModuleCard({ module }) {
  const { isModuleComplete, getModuleProgress } = useProgress();
  const { hasPurchased } = usePurchase();
  const Icon = iconMap[module.icon] || BookOpen;
  const isComplete = isModuleComplete(module.id);
  const moduleProgress = getModuleProgress(module.id);
  
  const isFreeModule = FREE_MODULE_IDS.includes(module.id);
  const isLocked = !isFreeModule && !hasPurchased;

  return (
    <Link
      to={`/module/${module.slug}`}
      data-testid={`module-card-${module.slug}`}
      className={cn(
        "group relative overflow-hidden rounded-2xl border-2 bg-white p-6 transition-all duration-300 card-hover",
        isComplete 
          ? "border-success/50 bg-success/5" 
          : isLocked
          ? "border-stone-200 hover:border-stone-300"
          : "border-stone-200 hover:border-primary/50"
      )}
    >
      {/* Completion badge */}
      {isComplete && (
        <div className="absolute top-4 right-4">
          <div className="bg-success text-white rounded-full p-1">
            <CheckCircle className="w-5 h-5" />
          </div>
        </div>
      )}

      {/* Locked badge */}
      {isLocked && !isComplete && (
        <div className="absolute top-4 right-4">
          <div className="bg-stone-200 text-stone-500 rounded-full p-1">
            <Lock className="w-5 h-5" />
          </div>
        </div>
      )}

      {/* Free badge */}
      {isFreeModule && !hasPurchased && !isComplete && (
        <div className="absolute top-4 right-4">
          <span className="bg-secondary text-secondary-foreground text-xs font-bold px-2 py-1 rounded-full">
            FREE
          </span>
        </div>
      )}

      {/* Module number */}
      <div className={cn(
        "absolute top-4 left-4 text-4xl font-heading font-bold transition-colors",
        isLocked ? "text-stone-100" : "text-stone-100 group-hover:text-primary-100"
      )}>
        {String(module.id).padStart(2, '0')}
      </div>

      {/* Content */}
      <div className="relative pt-8">
        <div className={cn(
          "w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-colors",
          isComplete 
            ? "bg-success/10 text-success" 
            : isLocked
            ? "bg-stone-100 text-stone-400"
            : "bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white"
        )}>
          {isLocked ? <Lock className="w-7 h-7" /> : <Icon className="w-7 h-7" />}
        </div>

        <h3 className={cn(
          "font-heading font-bold text-xl mb-2 transition-colors",
          isLocked ? "text-stone-500" : "text-text group-hover:text-primary"
        )}>
          {module.title}
        </h3>

        <p className={cn(
          "text-sm mb-4 line-clamp-2",
          isLocked ? "text-stone-400" : "text-text-muted"
        )}>
          {module.description}
        </p>

        {/* Progress bar */}
        {moduleProgress > 0 && !isComplete && !isLocked && (
          <div className="mb-4">
            <div className="flex justify-between text-xs text-text-muted mb-1">
              <span>Progress</span>
              <span>{moduleProgress}%</span>
            </div>
            <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary rounded-full transition-all duration-500"
                style={{ width: `${moduleProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between text-sm">
          <div className={cn(
            "flex items-center gap-1",
            isLocked ? "text-stone-400" : "text-text-muted"
          )}>
            <Clock className="w-4 h-4" />
            <span>{module.estimatedTime}</span>
          </div>

          <div className={cn(
            "flex items-center gap-1 font-medium group-hover:gap-2 transition-all",
            isLocked ? "text-stone-400" : "text-primary"
          )}>
            <span>{isComplete ? 'Review' : isLocked ? 'Locked' : 'Start'}</span>
            {isLocked ? <Lock className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </div>
        </div>
      </div>
    </Link>
  );
}
